import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Container, Box, Typography, Chip, TextField, MenuItem, CircularProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Tooltip,
  InputAdornment, ToggleButton, ToggleButtonGroup, Collapse, IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { getStripeSplits } from '../apollo/queries'
import { refundStripeOrder } from '../apollo/mutations'

const GET_SPLITS = gql`${getStripeSplits}`
const REFUND = gql`${refundStripeOrder}`

// Platform (RushmyFood) = indigo, Restaurant = emerald, same as the QOR pages.
const PLATFORM = '#5b5bd6'
const RESTAURANT = '#12a594'
const INK = '#1c2024'
const MUTED = '#697177'
const LINE = '#e8eaed'
const RED = '#a12226'

const STATUS_META = {
  PENDING: { label: 'Charged · restaurant not paid yet', bg: '#fdf4e3', fg: '#8a5a00' },
  SETTLED: { label: 'Restaurant paid', bg: '#e9f6ee', fg: '#166534' },
  REFUNDED: { label: 'Refunded', bg: '#fdebec', fg: RED },
  DISPUTED: { label: 'Disputed', bg: '#fdebec', fg: RED },
  VOIDED: { label: 'Voided', bg: '#f0f0f3', fg: '#5a5c66' },
}

const SCENARIO_META = {
  PLATFORM_DELIVERY: { label: 'We deliver', Icon: DeliveryDiningIcon },
  RESTAURANT_DELIVERY: { label: 'Restaurant delivers', Icon: StorefrontIcon },
  PICKUP: { label: 'Pickup', Icon: ShoppingBagIcon },
}

const usd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`)
const when = (v) => (v ? new Date(v).toLocaleString() : '—')

const PAGE = 25

// One "label …dotted leader… amount" line inside a panel. Zero rows are hidden
// unless they're the panel's total, so a pickup order doesn't list a $0 tip.
function MoneyRow({ label, value, hint, strong, color, negative }) {
  if (!strong && (value == null || Number(value) === 0)) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.4 }}>
      <Typography variant="body2" sx={{ color: strong ? INK : MUTED, fontWeight: strong ? 700 : 400, minWidth: 0 }}>
        {label}
        {hint && <Typography component="span" variant="caption" sx={{ color: MUTED, ml: 0.6 }}>{hint}</Typography>}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 12, borderBottom: `1px dotted ${LINE}`, transform: 'translateY(-4px)' }} />
      <Typography variant="body2" sx={{
        fontWeight: strong ? 800 : 600, color: color || INK,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>
        {negative ? `−${usd(value)}` : usd(value)}
      </Typography>
    </Box>
  )
}

/**
 * The expanded breakdown: what the customer paid, then where each dollar went.
 * The three panels always reconcile to the order total, which is the point —
 * the summary row alone can't show why a restaurant received less than its cut.
 */
function SplitDetail({ row }) {
  const pb = row.platformBreakdown || {}
  const rb = row.restaurantBreakdown || {}
  const total = Number(row.grandTotal) || 0
  const withheld = Number(row.debtRecovered) || 0
  const refunded = Number(row.refundedAmount) || 0
  const fee = Number(row.processorFee) || 0
  // The card processing fee the restaurant paid out of its share, and the rate
  // the order was placed with (none on orders placed before the fee existed).
  const processingFee = Number(pb.processingFee) || 0
  const rateHint = row.processingFeePercent != null
    ? `(${Number(row.processingFeePercent)}% + $${Number(row.processingFeeFixed || 0).toFixed(2)})`
    : null

  // What each side ends up with once debt withheld from the restaurant moves
  // across to us. These are the numbers the summary row shows.
  const restaurantPaid = Number(row.restaurantCut || 0) - withheld
  const platformKept = Number(row.platformCut || 0) + withheld
  const restPct = total > 0 ? Math.round((restaurantPaid / total) * 100) : 0
  const platPct = total > 0 ? 100 - restPct : 0

  const panel = {
    flex: 1, minWidth: 260, borderRadius: 2.5, p: 2.25,
    border: `1px solid ${LINE}`, bgcolor: '#fff',
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5 }}>
      {/* Proportion bar: how the customer's total divided between the two sides */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: RESTAURANT }}>
            Restaurant {usd(restaurantPaid)} · {restPct}%
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: PLATFORM }}>
            RushmyFood {usd(platformKept)} · {platPct}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: '#f0f0f3' }}>
          <Box sx={{ width: `${restPct}%`, bgcolor: RESTAURANT, transition: 'width .4s' }} />
          <Box sx={{ width: `${platPct}%`, bgcolor: PLATFORM, transition: 'width .4s' }} />
        </Box>
      </Box>

      {withheld > 0 && (
        <Box sx={{ mb: 2, px: 1.75, py: 1.25, borderRadius: 2, bgcolor: '#fdebec', border: '1px solid #f5c6c9' }}>
          <Typography variant="caption" sx={{ color: RED, fontWeight: 600, display: 'block' }}>
            {usd(withheld)} of the restaurant’s {usd(row.restaurantCut)} share was withheld against what they
            owe RushmyFood, so {usd(restaurantPaid)} was transferred. That is why the two shares in the summary
            row do not add up to the order total on their own.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* What the customer paid */}
        <Box sx={{ ...panel, bgcolor: '#fafbfc' }}>
          <Typography variant="overline" sx={{ color: MUTED, letterSpacing: 1 }}>Customer paid</Typography>
          <MoneyRow label="Items subtotal" value={row.subtotal} />
          <MoneyRow label="Delivery fee" value={row.deliveryFee} />
          <MoneyRow label="Tip" value={row.deliveryTip} />
          <MoneyRow label="Tax" value={row.tax} />
          <MoneyRow label="Service fee" value={row.serviceFee} />
          <MoneyRow label="Paid from wallet" hint="(RushmyFood credit)" value={row.walletCredit} negative />
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Charged to card" value={row.grandTotal} strong />
          </Box>
          {refunded > 0 && <MoneyRow label="Refunded since" value={refunded} color={RED} negative />}
        </Box>

        {/* Restaurant's share */}
        <Box sx={{ ...panel, borderTop: `3px solid ${RESTAURANT}` }}>
          <Typography variant="overline" sx={{ color: RESTAURANT, letterSpacing: 1, fontWeight: 700 }}>
            Restaurant receives
          </Typography>
          <MoneyRow
            label="Items subtotal"
            hint={row.commissionRate != null ? `(after ${Number(row.commissionRate)}% commission)` : null}
            value={rb.subtotalShare}
          />
          <MoneyRow label="Delivery fee" hint="(their driver)" value={rb.deliveryFee} />
          <MoneyRow label="Tip" hint="(their driver)" value={rb.deliveryTip} />
          <MoneyRow label="Tax" value={rb.tax} />
          <MoneyRow label="Server tip" hint="(their staff)" value={rb.other} />
          <MoneyRow label="Service fee" hint="(paid by the customer)" value={rb.flatFee} />
          <MoneyRow label="Processing fee" hint={rateHint} value={processingFee || null} color={RED} negative />
          <MoneyRow label="Store credit spent" hint="(their refund, their cost)" value={Math.abs(Number(rb.storeCredit)) || null} color={RED} negative />
          <MoneyRow label="Covered by RushmyFood" value={rb.coveredByPlatform} />
          <MoneyRow label="Withheld" hint="(owed to RushmyFood)" value={withheld || null} color={RED} negative />
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Transferred to restaurant" value={restaurantPaid} strong color={RESTAURANT} />
          </Box>
        </Box>

        {/* Platform's share */}
        <Box sx={{ ...panel, borderTop: `3px solid ${PLATFORM}` }}>
          <Typography variant="overline" sx={{ color: PLATFORM, letterSpacing: 1, fontWeight: 700 }}>
            RushmyFood receives
          </Typography>
          <MoneyRow
            label="Commission"
            hint={row.commissionRate != null ? `(${Number(row.commissionRate)}% of subtotal)` : null}
            value={pb.commission}
          />
          <MoneyRow label="Service fee" value={pb.flatFee} />
          <MoneyRow label="Processing fee" hint="(paid by the restaurant)" value={processingFee || null} />
          <MoneyRow label="Delivery fee" hint="(our rider)" value={pb.deliveryFee} />
          <MoneyRow label="Rider tip" hint="(passed to the rider)" value={pb.deliveryTip} />
          <MoneyRow label="Tax" hint="(we remit)" value={pb.tax} />
          <MoneyRow label="Wallet credit funded" value={row.walletCredit || null} negative />
          <MoneyRow label="Withheld from restaurant" value={withheld || null} />
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Total to RushmyFood" value={platformKept} strong color={PLATFORM} />
          </Box>
          <MoneyRow label="Stripe fee" hint="(our cost)" value={fee || null} color={RED} negative />
          {fee > 0 && (
            <MoneyRow label="After Stripe's fee" value={platformKept - fee} strong color={PLATFORM} />
          )}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1.5 }}>
        {usd(restaurantPaid)} to the restaurant + {usd(platformKept)} to RushmyFood = {usd(total)} charged.
        Stripe’s {usd(fee)} comes out of the RushmyFood side, never the restaurant’s.
      </Typography>
    </Box>
  )
}

export default function StripePayments() {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [refundRow, setRefundRow] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [toast, setToast] = useState(null)

  const { data, loading, error, refetch } = useQuery(GET_SPLITS, {
    variables: { settlementStatus: status || undefined, limit: PAGE, offset },
    fetchPolicy: 'cache-and-network',
  })
  const [refund, { loading: refunding }] = useMutation(REFUND)

  const page = data?.stripeSplits
  const rows = (page?.rows || []).filter((r) =>
    !search ||
    (r.orderNumber || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.restaurantName || '').toLowerCase().includes(search.toLowerCase()),
  )
  const count = page?.count || 0

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: INK }}>
        Stripe Payments
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, maxWidth: 820 }}>
        One row per Stripe order: what the customer paid, what RushmyFood keeps, and what the
        restaurant is transferred once the order finishes. Open a row for the full breakdown. The
        Stripe fee is our cost and is never shown to a restaurant.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2.5, mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small" select label="Status" value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0) }}
          sx={{ width: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="PENDING">Charged, not paid out</MenuItem>
          <MenuItem value="SETTLED">Restaurant paid</MenuItem>
          <MenuItem value="REFUNDED">Refunded</MenuItem>
          <MenuItem value="DISPUTED">Disputed</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="Search order or restaurant"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 280 }}
        />
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {count === 0 ? 'No payments' : `${offset + 1}–${Math.min(offset + PAGE, count)} of ${count}`}
        </Typography>
        <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
        <Button size="small" disabled={offset + PAGE >= count} onClick={() => setOffset(offset + PAGE)}>Next</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      {rows.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center', color: MUTED, border: `1px solid ${LINE}`, borderRadius: 2 }}>
          Nothing here yet.
        </Box>
      )}

      {rows.map((row) => {
        const meta = STATUS_META[row.settlementStatus] || { label: row.settlementStatus, bg: '#f0f0f3', fg: MUTED }
        const scenario = SCENARIO_META[row.scenario] || { label: row.scenario, Icon: ShoppingBagIcon }
        const { Icon } = scenario
        const open = openId === row._id
        const withheld = Number(row.debtRecovered) || 0
        const restaurantPaid = Number(row.restaurantCut || 0) - withheld
        const platformKept = Number(row.platformCut || 0) + withheld
        return (
          <Box
            key={row._id}
            sx={{
              borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: '#fff', mb: 1.5,
              boxShadow: open ? '0 8px 28px rgba(28,32,36,.09)' : '0 1px 2px rgba(28,32,36,.04)',
              transition: 'box-shadow .2s, border-color .2s',
              '&:hover': { borderColor: '#d5d8dc' },
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => setOpenId(open ? null : row._id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, md: 3 }, py: 1.75, cursor: 'pointer', flexWrap: 'wrap' }}
            >
              <Box sx={{ minWidth: 180 }}>
                <Typography sx={{ fontWeight: 700, color: INK }}>
                  #{row.orderNumber || row.orderId}
                </Typography>
                <Typography variant="caption" sx={{ color: MUTED }}>
                  {row.restaurantName} · {when(row.createdAt)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 170 }}>
                <Icon sx={{ fontSize: 16, color: MUTED }} />
                <Typography variant="caption" sx={{ color: MUTED }}>{scenario.label}</Typography>
              </Box>

              <Chip size="small" label={meta.label}
                sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: meta.bg, color: meta.fg }} />
              {withheld > 0 && (
                <Tooltip title="Withheld from their share against what they owe RushmyFood">
                  <Chip size="small" label={`Withheld ${usd(withheld)}`}
                    sx={{ height: 22, fontSize: 12, bgcolor: '#fdebec', color: RED, fontWeight: 700 }} />
                </Tooltip>
              )}
              {Number(row.refundedAmount) > 0 && (
                <Chip size="small" label={`Refunded ${usd(row.refundedAmount)}`}
                  sx={{ height: 22, fontSize: 12, bgcolor: '#fdebec', color: RED }} />
              )}
              {Number(row.reversedAmount) > 0 && (
                <Tooltip title="Taken back out of the restaurant's transfer">
                  <Chip size="small" label={`Clawed back ${usd(row.reversedAmount)}`}
                    sx={{ height: 22, fontSize: 12, bgcolor: '#f0f0f3', color: MUTED }} />
                </Tooltip>
              )}

              <Box sx={{ flex: 1 }} />

              <Box sx={{ textAlign: 'right', minWidth: 104 }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>Customer paid</Typography>
                <Typography sx={{ fontWeight: 700, color: INK }}>{usd(row.grandTotal)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 104 }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>RushmyFood</Typography>
                <Typography sx={{ fontWeight: 700, color: PLATFORM }}>{usd(platformKept)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 104 }}>
                <Typography sx={{ fontSize: 12, color: MUTED }}>Restaurant</Typography>
                <Typography sx={{ fontWeight: 700, color: RESTAURANT }}>{usd(restaurantPaid)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 92 }}>
                <Tooltip title="What Stripe charged us on this payment">
                  <Box>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>Stripe fee</Typography>
                    <Typography sx={{ fontWeight: 600, color: MUTED }}>{usd(row.processorFee)}</Typography>
                  </Box>
                </Tooltip>
              </Box>

              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setRefundRow(row) }}>
                Refund
              </Button>
              <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                <KeyboardArrowDownIcon />
              </IconButton>
            </Box>

            <Collapse in={open} timeout={250} unmountOnExit>
              <SplitDetail row={row} />
              {(row.transferId || row.disputeId) && (
                <Typography variant="caption" sx={{ color: MUTED, display: 'block', px: { xs: 2, md: 3 }, pb: 2 }}>
                  {row.transferId && <>Transfer {row.transferId} · {when(row.transferredAt)}</>}
                  {row.transferId && row.disputeId && ' · '}
                  {row.disputeId && <>Dispute {row.disputeId} ({row.disputeStatus || 'open'})</>}
                </Typography>
              )}
            </Collapse>
          </Box>
        )
      })}

      <RefundDialog
        row={refundRow}
        busy={refunding}
        onClose={() => setRefundRow(null)}
        onSubmit={async ({ amount, chargeTo, note }) => {
          try {
            const res = await refund({
              variables: { orderId: refundRow.orderId, amount, chargeTo, note: note || undefined },
            })
            const r = res?.data?.refundStripeOrder
            setRefundRow(null)
            await refetch()
            setToast({ severity: r?.ok ? 'success' : 'error', text: r?.message || 'Refund failed.' })
          } catch (e) {
            setToast({ severity: 'error', text: e.message })
          }
        }}
      />

      <Snackbar open={!!toast} autoHideDuration={8000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert> : null}
      </Snackbar>
    </Container>
  )
}

/**
 * Refunding asks two questions: how much, and who carries it. "RushmyFood" is
 * the default because taking money back from a restaurant should be deliberate.
 */
function RefundDialog({ row, busy, onClose, onSubmit }) {
  const remaining = row ? Number(row.grandTotal || 0) - Number(row.refundedAmount || 0) : 0
  const [amount, setAmount] = useState('')
  const [chargeTo, setChargeTo] = useState('PLATFORM')
  const [note, setNote] = useState('')

  React.useEffect(() => {
    if (row) {
      setAmount(remaining.toFixed(2))
      setChargeTo('PLATFORM')
      setNote('')
    }
  }, [row, remaining])

  if (!row) return null
  const value = parseFloat(amount)
  const invalid = !(value > 0) || value > remaining + 0.001

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Refund order #{row.orderNumber || row.orderId}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
          {usd(remaining)} of {usd(row.grandTotal)} can still be refunded. Stripe keeps its original
          fee on a refund either way.
        </Typography>
        <TextField
          size="small" fullWidth label="Amount" type="number" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={{ mb: 2 }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK, mb: 0.75 }}>Who carries it</Typography>
        <ToggleButtonGroup
          size="small" exclusive fullWidth value={chargeTo}
          onChange={(_, v) => v && setChargeTo(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="PLATFORM">RushmyFood</ToggleButton>
          <ToggleButton value="RESTAURANT">Restaurant</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 2 }}>
          {chargeTo === 'RESTAURANT'
            ? 'Taken back out of their transfer. If the money already reached their bank, it is added to what they owe and withheld from their next payout.'
            : 'RushmyFood absorbs it, and the restaurant keeps its share.'}
        </Typography>
        <TextField
          size="small" fullWidth label="Note (optional)" value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" color="error" disabled={busy || invalid}
          onClick={() => onSubmit({ amount: value, chargeTo, note })}
        >
          {busy ? <CircularProgress size={18} color="inherit" /> : `Refund ${usd(value || 0)}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
